import axios from 'axios';
import { getAuthHeader } from './authService';
import API_CONFIG from '../config';

const API_URL = `${API_CONFIG.BASE_URL}/categories`;
const FILE_UPLOAD_URL = `${API_CONFIG.BASE_URL}/uploads/images`;
const FILE_URL = `${API_CONFIG.BASE_URL}/uploads/images`;

/**
 * Service for managing product categories
 */
const categoryService = {
  /**
   * Get all categories with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (0-based)
   * @param {number} params.size - Page size
   * @param {string} params.sort - Field to sort by
   * @param {string} params.direction - Sort direction (ASC or DESC)
   * @returns {Promise<Object>} Paginated category data
   */
  getAllCategories: async (params = { page: 0, size: 100, sort: 'name', direction: 'ASC' }) => {
    const response = await axios.get(API_URL, {
      params,
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Get categories by company ID
   * @param {number} companyId - Company ID
   * @returns {Promise<Array>} List of categories
   */
  getCategoriesByCompany: async (companyId) => {
    try {
      if (!companyId) {
        console.error('Company ID is required but was not provided');
        throw new Error('Company ID is required');
      }

      console.log('Fetching categories for company:', companyId);
      const headers = getAuthHeader();
      console.log('Using auth headers:', headers);

      const response = await axios.get(`${API_URL}/company/${companyId}`, {
        headers: headers
      });

      console.log('Categories response:', response.data);

      if (!response.data) {
        console.warn('No categories returned from API');
        return [];
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error.response || error);
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to access these categories.');
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch categories. Please try again.');
    }
  },

  /**
   * Get root categories (categories without parent)
   * @param {number} companyId - Company ID
   * @returns {Promise<Array>} List of root categories
   */
  getRootCategories: async (companyId) => {
    const response = await axios.get(`${API_URL}/root/company/${companyId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Get subcategories of a parent category
   * @param {number} parentId - Parent category ID
   * @returns {Promise<Array>} List of subcategories
   */
  getSubcategories: async (parentId) => {
    const response = await axios.get(`${API_URL}/${parentId}/subcategories`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Get a category by ID
   * @param {number} id - Category ID
   * @returns {Promise<Object>} Category details
   */
  getCategoryById: async (id) => {
    const response = await axios.get(`${API_URL}/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Create a new category
   * @param {Object} categoryData - Category data
   * @returns {Promise<Object>} Created category
   */
  createCategory: async (categoryData) => {
    const response = await axios.post(API_URL, categoryData, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Update a category
   * @param {number} id - Category ID
   * @param {Object} categoryData - Updated category data
   * @returns {Promise<Object>} Updated category
   */
  updateCategory: async (id, categoryData) => {
    const response = await axios.put(`${API_URL}/${id}`, categoryData, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Delete a category
   * @param {number} id - Category ID
   * @returns {Promise<void>}
   */
  deleteCategory: async (id) => {
    await axios.delete(`${API_URL}/${id}`, {
      headers: getAuthHeader()
    });
  },

  /**
   * Upload a category image
   * @param {File} imageFile - The image file to upload
   * @returns {Promise<string>} - Promise with the image URL
   */
  uploadCategoryImage: async (imageFile) => {
    try {
      const formData = new FormData();
      formData.append('file', imageFile);

      const response = await axios.post(FILE_UPLOAD_URL, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (!response.data || !response.data.filename) {
        throw new Error('Invalid response from server');
      }

      return response.data.filename;
    } catch (error) {
      console.error('Error uploading category image:', error.response?.data || error);
      throw error;
    }
  },

  /**
   * Get the full URL for a category image
   * @param {string} filename - The image filename
   * @returns {string} - The full URL to access the image
   */
  getCategoryImageUrl: (filename) => {
    if (!filename) return '/images/placeholder-category.png';
    return `${FILE_URL}/${filename}`;
  },

  /**
   * Build a hierarchical category tree from flat category list
   * @param {Array} categories - Flat list of categories
   * @returns {Object} Object containing root categories and subcategories map
   */
  buildCategoryTree: (categories) => {
    // Extract root categories (those without parent)
    const rootCategories = categories.content?.filter(c => !c.parentCategory) || [];
    
    // Build subcategories map
    const categoryMap = new Map();
    
    categories.content?.forEach(category => {
      if (category.parentCategory) {
        if (!categoryMap.has(category.parentCategory.id)) {
          categoryMap.set(category.parentCategory.id, []);
        }
        categoryMap.get(category.parentCategory.id).push(category);
      }
    });
    
    return {
      rootCategories,
      subcategories: Object.fromEntries(categoryMap)
    };
  },

  /**
   * Format category path for display (e.g., "Electronics > Laptops")
   * @param {Object} category - Category object
   * @param {Object} allCategories - Map of all categories
   * @returns {string} Formatted category path
   */
  formatCategoryPath: (category, allCategories) => {
    if (!category) return '';
    
    const path = [];
    let current = category;
    
    while (current) {
      path.unshift(current.name);
      current = current.parentCategory;
    }
    
    return path.join(' > ');
  }
};

export default categoryService; 